import { Fragment, HTMLAttributes, KeyboardEvent } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Archive, ArchiveRestore, Bookmark, Circle, Mail, MailOpen, MoreHorizontal, PanelRight } from "lucide-react";
import { INotificationListData } from "../../../_services/interface/notifications.interface";
import { dataCompleta, dataIso, tempoRelativo } from "../notificationOrigin";
import { linhaDeContexto, metaDaUrgencia, metaDoProduto, viewDoItem } from "../notificationView";

/**
 * GEN-4101 — ícone do aviso: o do produto (mesmo da sidebar), num quadrado na cor da urgência.
 * A urgência nunca é dada só pela cor: o rótulo vai no `aria-label`/tooltip.
 */
export const NotificationIcon = ({ item, size = 28 }: { item: INotificationListData; size?: number }) => {
  const view = viewDoItem(item);
  const produto = metaDoProduto(view.product);
  const urgencia = metaDaUrgencia(view.severity);
  const Icone = produto.icon;
  return (
    <Tooltip label={`${produto.label} · ${urgencia.label}`} openDelay={500}>
      <Flex
        flexShrink={0}
        w={`${size}px`}
        h={`${size}px`}
        borderRadius="7px"
        align="center"
        justify="center"
        bg={urgencia.bg}
        color={urgencia.color}
        borderWidth="1px"
        borderColor={urgencia.border}
        role="img"
        aria-label={`${produto.label}, ${urgencia.label}`}
      >
        <Icone size={Math.round(size * 0.55)} strokeWidth={1.8} />
      </Flex>
    </Tooltip>
  );
};

export interface NotificationRowProps {
  item: INotificationListData;
  /** "bell" = compacto, com ação principal e menu; "list" = lista da central, com seleção. */
  variant: "bell" | "list";
  isOpen?: boolean;
  isChecked?: boolean;
  dragProps?: HTMLAttributes<HTMLDivElement>;
  isDragging?: boolean;
  onToggleCheck?: () => void;
  /** Clique na linha (sino: vai ao objeto; lista: abre no painel de leitura). */
  onOpen: () => void;
  onPrimaryAction?: () => void;
  onToggleRead: () => void;
  onToggleSave?: () => void;
  onToggleArchive: () => void;
  /** Só no sino: "Ver na central". */
  onShowInInbox?: () => void;
}

export const NotificationRow = ({
  item,
  variant,
  isOpen,
  isChecked,
  onToggleCheck,
  onOpen,
  onPrimaryAction,
  onToggleRead,
  onToggleSave,
  onToggleArchive,
  onShowInInbox,
  dragProps,
  isDragging,
}: NotificationRowProps) => {
  const view = viewDoItem(item);
  // No sino, o foco do menu deve continuar dentro do Popover. Um Portal no body
  // faz o painel fechar por blur antes que a pessoa consiga escolher uma ação.
  const MenuContainer = variant === "bell" ? Fragment : Portal;
  const naoLida = !item.readInSystem;
  const arquivada = item.folder === "ARCHIVED";
  const contexto = linhaDeContexto(view);
  const relativo = tempoRelativo(item.createdAt) || item.createdAtFormatted || "";
  const quando = dataCompleta(item.createdAt) || item.createdAtFormatted || "";
  const urgencia = metaDaUrgencia(view.severity);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Flex
      {...dragProps}
      opacity={isDragging ? 0.45 : 1}
      role="group"
      position="relative"
      align="flex-start"
      gap="10px"
      px={variant === "bell" ? "14px" : "12px"}
      py="10px"
      cursor="pointer"
      // Estados da linha iguais aos da Caixa Postal (App.css `.dte-row-open` / `.dte-row-selected`).
      bg={isOpen ? "#DBDCFF" : isChecked ? "#E7E8FF" : "white"}
      boxShadow={isOpen ? "inset 4px 0 0 #4B4EFF" : isChecked ? "inset 3px 0 0 #4B4EFF" : undefined}
      _hover={{ bg: isOpen ? "#DBDCFF" : isChecked ? "#E7E8FF" : "#F7FAFC" }}
      _focusVisible={{ outline: "2px solid #4B4EFF", outlineOffset: "-2px" }}
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      aria-label={`${naoLida ? "Não lida. " : ""}${view.title}${contexto ? `. ${contexto}` : ""}`}
    >
      {variant === "list" && onToggleCheck ? (
        <Box pt="6px" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            isChecked={!!isChecked}
            onChange={onToggleCheck}
            aria-label={`Selecionar "${view.title}"`}
            size="md"
            className="checkbox-datatables"
            colorScheme="green"
          />
        </Box>
      ) : null}

      {variant === "list" ? (
        // GEN-4101 r2 — ações à ESQUERDA, sempre visíveis, como os `leftActions` da Caixa Postal
        // (DteListMessage): bolinha de leitura (cheia = não lida) e marcador de salvo.
        <Flex align="center" gap="10px" pt="6px" flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip
            label={naoLida ? "Não lida — clique para marcar como lida" : "Lida — clique para marcar como não lida"}
            placement="top"
            openDelay={300}
          >
            <Box
              as="button"
              type="button"
              aria-label={naoLida ? "Marcar como lida" : "Marcar como não lida"}
              display="inline-flex"
              onClick={onToggleRead}
            >
              <Circle
                size={12}
                color={naoLida ? "#4B4EFF" : "#CECECE"}
                fill={naoLida ? "#4B4EFF" : "none"}
                strokeWidth={naoLida ? 2 : 1.5}
              />
            </Box>
          </Tooltip>
          {onToggleSave ? (
            <Tooltip label={item.isImportant ? "Tirar dos salvos" : "Salvar"} placement="top" openDelay={300}>
              <Box
                as="button"
                type="button"
                aria-label={item.isImportant ? "Tirar dos salvos" : "Salvar"}
                display="inline-flex"
                onClick={onToggleSave}
              >
                <Bookmark
                  size={20}
                  color={item.isImportant ? "#F7CA4C" : "#CECECE"}
                  fill={item.isImportant ? "#F7CA4C" : "none"}
                />
              </Box>
            </Tooltip>
          ) : null}
        </Flex>
      ) : (
        // Sino: um sinal só de não lida (ponto + título semibold) — sem fundo colorido nem negrito em tudo.
        <Box w="8px" flexShrink={0} pt="10px" aria-hidden="true">
          {naoLida ? <Box w="8px" h="8px" borderRadius="full" bg="#4B4EFF" /> : null}
        </Box>
      )}

      <NotificationIcon item={item} />

      <Flex direction="column" flex="1" minW="0" gap="2px">
        <Flex align="baseline" gap="8px" minW="0">
          <Text
            flex="1"
            minW="0"
            fontSize="14px"
            fontWeight={naoLida ? "600" : "400"}
            color={naoLida ? "#171923" : "#46464A"}
            noOfLines={2}
          >
            {view.title}
          </Text>
          {relativo ? (
            <Tooltip label={quando} isDisabled={!quando} openDelay={400}>
              <Text
                as="time"
                dateTime={dataIso(item.createdAt) || undefined}
                flexShrink={0}
                fontSize="12px"
                color="#77777B"
                whiteSpace="nowrap"
              >
                {relativo}
              </Text>
            </Tooltip>
          ) : null}
        </Flex>

        {contexto ? (
          <Text fontSize="12px" color="#77777B" noOfLines={variant === "bell" ? 1 : 2} title={contexto}>
            {contexto}
          </Text>
        ) : null}

        {variant === "bell" && view.action && onPrimaryAction ? (
          <Flex mt="6px" gap="6px" align="center">
            <Button
              size="xs"
              h="26px"
              fontSize="12px"
              fontWeight="500"
              borderRadius="6px"
              bg="#4B4EFF"
              color="white"
              _hover={{ bg: "#282be0" }}
              onClick={(e) => {
                e.stopPropagation();
                onPrimaryAction();
              }}
            >
              {view.action.label}
            </Button>
            {view.severity !== "info" ? (
              <Text fontSize="11px" fontWeight="500" color={urgencia.color}>
                {urgencia.label}
              </Text>
            ) : null}
          </Flex>
        ) : null}
      </Flex>

      {
        <Box onClick={(e) => e.stopPropagation()} flexShrink={0}>
          <Menu placement="bottom-end" isLazy>
            <Tooltip label="Mais ações" openDelay={400}>
              <MenuButton
                as={IconButton}
                aria-label={`Mais ações para "${view.title}"`}
                icon={<MoreHorizontal size={16} />}
                size="xs"
                variant="ghost"
                color="#46464A"
                _hover={{ bgColor: "#ECECFF", color: "#4B4EFF" }}
              />
            </Tooltip>
            <MenuContainer>
              <MenuList minW="200px" py={1} zIndex={1500}>
                {onShowInInbox ? (
                  <MenuItem icon={<PanelRight size={16} color="#718096" />} fontSize="13px" onClick={onShowInInbox}>
                    Ver detalhes
                  </MenuItem>
                ) : null}
                <MenuItem
                  icon={naoLida ? <MailOpen size={16} color="#718096" /> : <Mail size={16} color="#718096" />}
                  fontSize="13px"
                  onClick={onToggleRead}
                >
                  {naoLida ? "Marcar como lida" : "Marcar como não lida"}
                </MenuItem>
                <MenuItem
                  icon={
                    arquivada ? <ArchiveRestore size={16} color="#718096" /> : <Archive size={16} color="#718096" />
                  }
                  fontSize="13px"
                  onClick={onToggleArchive}
                >
                  {arquivada ? "Voltar para a Entrada" : "Arquivar"}
                </MenuItem>
              </MenuList>
            </MenuContainer>
          </Menu>
        </Box>
      }
    </Flex>
  );
};
